import { onLive } from '../lib/live.ts';
import { netplay } from '../lib/netplay/session.ts';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me } from '@replay/shared';
import { api } from '../lib/api.ts';
import { useAuth, useMe } from '../lib/auth.tsx';
import { runAutoClean } from '../lib/cache.ts';
import { useNavAction } from '../lib/nav/react.tsx';
import { syncAllPending } from '../player/saveSync.ts';
import { audio, useAudioPrefs } from '../lib/audio/react.tsx';
import { Ambient } from './Ambient.tsx';
import { ConnectionPill, useInstallPrompt, useSyncNow } from './OfflineBits.tsx';
import { Icon, Logo } from './Icon.tsx';
import { Avatar, useLayerFocus, useToast } from './ui.tsx';

const TABS = [
  { to: '/', label: 'Inicio', icon: 'home', end: true },
  { to: '/library', label: 'Biblioteca', icon: 'library' },
  { to: '/friends', label: 'Amigos', icon: 'friends' },
  { to: '/messages', label: 'Mensajes', icon: 'chat' },
  { to: '/notifications', label: 'Avisos', icon: 'bell' },
] as const;

function useUnread() {
  return useQuery({ queryKey: ['unread'], queryFn: () => api<{ notifications: number; messages: number }>('/api/unread'), refetchInterval: 60_000 });
}

/** Live events (SSE): new messages and notifications refresh the relevant queries. */
function useLiveEvents() {
  const qc = useQueryClient();
  const me = useMe();
  useEffect(() => {
    void netplay.init(me.id);
  }, [me.id]);
  const toast = useToast();
  useEffect(() => {
    const offNotification = onLive('notification', (data) => {
      const n = data as { type: string; payload: Record<string, string> };
      qc.invalidateQueries({ queryKey: ['unread'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      if (n.type === 'friend_request') toast(`${n.payload.displayName} quiere ser tu amigo`);
      if (n.type === 'play_invite') toast(`${n.payload.fromName} te invita a jugar ${n.payload.roomId ? 'online ' : ''}a ${n.payload.title}`);
      if (n.type === 'friend_accepted') {
        toast(`${n.payload.displayName} aceptó tu solicitud`, 'ok');
        qc.invalidateQueries({ queryKey: ['friends'] });
      }
    });
    const offMessage = onLive('message', (data) => {
      const m = data as { fromId: string; fromName: string };
      qc.invalidateQueries({ queryKey: ['unread'] });
      qc.invalidateQueries({ queryKey: ['messages'] });
      qc.invalidateQueries({ queryKey: ['thread', m.fromId] });
      if (!location.pathname.startsWith(`/messages/${m.fromId}`)) toast(`Mensaje de ${m.fromName}`);
    });
    // A friend came online: friend lists and profiles show it now.
    const offPresence = onLive('presence', () => {
      qc.invalidateQueries({ queryKey: ['friends'] });
      qc.invalidateQueries({ queryKey: ['profile'] });
    });
    return () => {
      offNotification();
      offMessage();
      offPresence();
    };
  }, [qc, toast]);
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  return <span className="cbar-clock">{now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>;
}

/** System menu: profile, settings and less frequent destinations (opened with Start or the avatar). */
function SystemMenu({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const { can, logout } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  useLayerFocus(ref);
  const tv = (me.settings as { tvMode?: boolean }).tvMode === true;
  const [sound, setSound] = useAudioPrefs();
  const install = useInstallPrompt();
  const sync = useSyncNow();
  const soundOn = sound.sfx || sound.ambient;
  const setTv = useMutation({
    mutationFn: (v: boolean) => api<Me>('/api/me', { method: 'PATCH', body: { settings: { tvMode: v } } }),
    onSuccess: (m) => qc.setQueryData(['me'], m),
  });
  const go = (to: string) => {
    onClose();
    nav(to);
  };
  return (
    <>
      <div className="sheet-backdrop" onMouseDown={onClose} />
      <div className="sysmenu" role="dialog" aria-label="Menú" data-nav-scope ref={ref}>
        <button className="sr-only" data-nav-close onClick={onClose} tabIndex={-1}>
          Cerrar
        </button>
        <div className="who">
          <Avatar name={me.displayName} url={me.avatarUrl} />
          <div>
            <strong>{me.displayName}</strong>
            <span>@{me.username}</span>
          </div>
        </div>
        <button className="menu-item" onClick={() => go(`/u/${me.username}`)} data-autofocus>
          <Icon name="friends" /> Mi perfil
        </button>
        <button className="menu-item" onClick={() => go('/settings')}>
          <Icon name="settings" /> Ajustes
        </button>
        <button className="menu-item" onClick={() => go('/settings/saves')}>
          <Icon name="save" /> Mis partidas
        </button>
        <button className="menu-item" onClick={() => go('/requests')}>
          <Icon name="inbox" /> Pedir un juego
        </button>
        {(can('invites.create') || can('invites.manage')) && (
          <button className="menu-item" onClick={() => go('/invites')}>
            <Icon name="ticket" /> Invitar
          </button>
        )}
        {can('admin.access') && (
          <button className="menu-item" onClick={() => go('/admin')}>
            <Icon name="shield" /> Administración
          </button>
        )}
        <button className="menu-item" onClick={() => setTv.mutate(!tv)} aria-pressed={tv}>
          <Icon name="tv" /> Modo televisión <span className="end">{tv ? 'Activado' : 'Desactivado'}</span>
        </button>
        <button className="menu-item" onClick={() => void sync.run()} disabled={sync.busy}>
          <Icon name="cloud" /> Sincronizar partidas
        </button>
        {install.canInstall && (
          <button
            className="menu-item"
            onClick={async () => {
              onClose();
              await install.install();
            }}
          >
            <Icon name="download" /> Instalar REPLAY en este dispositivo
          </button>
        )}
        <button className="menu-item" onClick={() => setSound(soundOn ? { sfx: false, ambient: false } : { sfx: true, ambient: true })} aria-pressed={soundOn}>
          <Icon name="bell" /> Sonido <span className="end">{soundOn ? 'Activado' : 'Silenciado'}</span>
        </button>
        <button
          className="menu-item danger"
          onClick={async () => {
            onClose();
            await logout();
          }}
        >
          <Icon name="logout" /> Cerrar sesión
        </button>
      </div>
    </>
  );
}

export function Shell() {
  const me = useMe();
  const nav = useNavigate();
  const loc = useLocation();
  const unread = useUnread().data;
  const [menu, setMenu] = useState(false);
  const [solid, setSolid] = useState(false);
  useLiveEvents();
  // The ambient bed belongs to the signed-in app only (never login or invitation pages).
  useEffect(() => {
    audio.allowAmbient(true);
    return () => audio.allowAmbient(false);
  }, []);

  // Upload saves made offline in earlier sessions; apply local cache cleanup.
  useEffect(() => {
    void syncAllPending(me.id);
    void runAutoClean().catch(() => {});
    const onOnline = () => void syncAllPending(me.id);
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [me.id]);

  // TV mode: explicit setting, else automatic on very large screens.
  useEffect(() => {
    const tv = (me.settings as { tvMode?: boolean }).tvMode;
    document.documentElement.dataset.tv = tv ? 'true' : 'auto';
  }, [me.settings]);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMenu(false), [loc.pathname]);

  // LB / RB (or PageUp / PageDown) cycle the main sections.
  useEffect(() => {
    const onTabs = (e: Event) => {
      const dir = (e as CustomEvent<number>).detail;
      const idx = TABS.findIndex((t) => ('end' in t && t.end ? loc.pathname === t.to : loc.pathname.startsWith(t.to)));
      const next = TABS[(Math.max(idx, 0) + dir + TABS.length) % TABS.length]!;
      nav(next.to);
    };
    window.addEventListener('replay:tabs', onTabs);
    return () => window.removeEventListener('replay:tabs', onTabs);
  }, [loc.pathname, nav]);

  // Start / Menu key toggles the system menu.
  useNavAction((a) => {
    if (a === 'menu') {
      setMenu((v) => !v);
      return true;
    }
  });

  const badge = (to: string) => (to === '/messages' ? unread?.messages : to === '/notifications' ? unread?.notifications : 0);

  return (
    <>
      <Ambient />
      <header className={`cbar${solid ? ' solid' : ''}`} data-nav-zone="bar">
        <NavLink to="/" className="wordmark" aria-label="REPLAY, inicio">
          <Logo />
          REPLAY
        </NavLink>
        <nav className="cbar-tabs" aria-label="Secciones">
          <span className="glyph glyph-lb" aria-hidden="true">
            LB
          </span>
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={'end' in t && t.end}>
              {t.label}
              {!!badge(t.to) && <span className="badge">{badge(t.to)}</span>}
            </NavLink>
          ))}
          <span className="glyph glyph-rb" aria-hidden="true">
            RB
          </span>
        </nav>
        <div className="cbar-right">
          <ConnectionPill />
          <Clock />
          <button className="icon-btn" aria-label="Buscar" onClick={() => nav('/library?search=1')}>
            <Icon name="search" />
          </button>
          <button className="me-btn" aria-label="Menú" aria-expanded={menu} onClick={() => setMenu((v) => !v)}>
            <Avatar name={me.displayName} url={me.avatarUrl} />
          </button>
        </div>
      </header>

      <main className="stage" id="main" key={loc.pathname.split('/')[1] ?? ''}>
        <Outlet />
      </main>

      <nav className="tabbar" aria-label="Secciones">
        <NavLink to="/" end>
          <Icon name="home" />
          Inicio
        </NavLink>
        <NavLink to="/library">
          <Icon name="library" />
          Biblioteca
        </NavLink>
        <NavLink to="/friends">
          <Icon name="friends" />
          Amigos
          {!!unread?.messages && <span className="badge">{unread.messages}</span>}
        </NavLink>
        <NavLink to="/notifications">
          <Icon name="bell" />
          Avisos
          {!!unread?.notifications && <span className="badge">{unread.notifications}</span>}
        </NavLink>
      </nav>

      {menu && <SystemMenu onClose={() => setMenu(false)} />}
    </>
  );
}

/** Conventional padded page for non-console screens (settings, social, admin). */
export function Page() {
  return (
    <div className="page">
      <Outlet />
    </div>
  );
}
